import * as THREE from 'three';
import { TILE_SIZE } from '../../labyrinth/Labyrinth';
import { applyStylizedShading, type LightmapBindings } from '../StylizedShading';

export const WHEEL_R   = TILE_SIZE * 0.13;
export const WHEEL_LEN = TILE_SIZE * 0.10;
/** Y-center of a wheel so it sits flush on the floor. */
export const WHEEL_Y   = WHEEL_R;

const COLOR = 0x14161b;

const SHADING = {
  lightmapNormalOffset: false,
  keyLightStrength: 0.30,
  rimStrength: 0.55,
  rimPower: 2.5,
  verticalAOStrength: 0.22,
} as const;

const SKIN = {
  panelScale: 1.30, etchStrength: 0.55, ledDensity: 0.02,
  conduitStrength: 0.45, trimHue: 0.52, // cyan traction rings
} as const;

export interface RobotWheelPart {
  /** One wheel mesh — the rig positions each instance on the chassis. */
  readonly object: THREE.Mesh;
  /** Roll angle around the wheel's local Z axis (axle). Negative = forward roll
   *  for local +X forward. */
  setSpin(rad: number): void;
  dispose(): void;
}

/**
 * Procedural wheel cylinder. Call once per wheel — the rig places 6 (3 per
 * side) and drives left/right spin independently for skid-steering. To swap
 * with a Blender model, replace the factory body; keep WHEEL_R / WHEEL_LEN /
 * WHEEL_Y in sync and have `setSpin` rotate the mesh about its axle.
 */
export function createRobotWheel(lightmap: LightmapBindings): RobotWheelPart {
  const geo = new THREE.CylinderGeometry(WHEEL_R, WHEEL_R, WHEEL_LEN, 16);
  // Cylinder default axis is Y; rotate so the axle lies along Z (lateral).
  geo.rotateX(Math.PI / 2);
  const mat = new THREE.MeshBasicMaterial({ color: COLOR });
  applyStylizedShading(mat, { lightmap, ...SHADING, skin: { seed: 41, ...SKIN } });
  const mesh = new THREE.Mesh(geo, mat);
  return {
    object: mesh,
    setSpin: (rad: number) => { mesh.rotation.z = rad; },
    dispose: () => { geo.dispose(); mat.dispose(); },
  };
}
